import { useContext, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { useQuery } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import MasterControlBySwitch from 'shared/components/commonComponentsMC/MasterControlBySwitch';
import SwitchLayout from './SwitchLayout';

import { selectMCIsExpanded } from 'shared/slice/MCIsExpandedSlice';
import { selectUnits } from 'shared/slice/unitsSlice';
import { handleAccessToken, selectUserPermissions } from 'shared/slice/userSlice';
import { handleResetAllSelectBySwitch } from 'shared/slice/masterControlBySwitchSelectSlice';
import { handleResetAllSelectByLocation } from 'shared/slice/masterControlSelectByLocationSlice';

import {
  selectEssSwitch,
  essSpecificLocationUnselectMachinesHandler,
  handleOpenMachineController as essHandleOpenMachineController,
  handleUnSelectIndividualMachine as essHandleUnSelectIndividualMachine,
} from 'shared/slice/essSwitchSlice';
import {
  selectTgsSwitch,
  tgsSpecificLocationUnselectMachinesHandler,
  tgsHandleOpenMachineController,
  tgsHandleUnSelectIndividualMachine,
} from 'shared/slice/tgsSwitchSlice';
import {
  selectTesSwitch,
  tesSpecificLocationUnselectMachinesHandler,
  tesHandleOpenMachineController,
  tesHandleUnSelectIndividualMachine,
} from 'shared/slice/tesSwitchSlice';

import { getEssZones, getTgsZones, getTesZones } from 'shared/services';
import {
  useGetScheduleQueries,
  useGetThermocouplesQueries,
  useSetZoneOpeningsState,
} from 'shared/hooks';
import { useSetOpenMasterControl } from 'shared/hooks/ess_tgs_tes_hooks/useSetOpenMasterControl';
import { EssTgsTesContext } from 'shared/context/contextOfEssTgsTes';
import { loopMachinesHandler } from 'shared/helpers/ess-tgs-tes-mc';
import { filterZonesWithDevices } from 'shared/helpers/locationsCache';

const SYSTEM_HANDLERS = {
  ess: {
    handleOpenMachineController: essHandleOpenMachineController,
    handleUnSelectIndividualMachine: essHandleUnSelectIndividualMachine,
    specificLocationUnselectMachinesHandler: essSpecificLocationUnselectMachinesHandler,
  },
  tgs: {
    handleOpenMachineController: tgsHandleOpenMachineController,
    handleUnSelectIndividualMachine: tgsHandleUnSelectIndividualMachine,
    specificLocationUnselectMachinesHandler: tgsSpecificLocationUnselectMachinesHandler,
  },
  tes: {
    handleOpenMachineController: tesHandleOpenMachineController,
    handleUnSelectIndividualMachine: tesHandleUnSelectIndividualMachine,
    specificLocationUnselectMachinesHandler: tesSpecificLocationUnselectMachinesHandler,
  },
};

const SwitchMain = ({ swtName, isMasterControl }) => {
  const isMobile = useMediaQuery({ query: '(max-width:600px)' });
  const { isF } = useSelector(selectUnits);
  const permissions = useSelector(selectUserPermissions);
  const MCIsExpanded = useSelector(selectMCIsExpanded);
  const { masterControl } = MCIsExpanded[swtName];

  const essData = useSelector(selectEssSwitch);
  const tgsData = useSelector(selectTgsSwitch);
  const tesData = useSelector(selectTesSwitch);

  const switchData =
    swtName === 'ess' ? essData.essSwitch : swtName === 'tgs' ? tgsData.tgsSwitch : tesData.tesSwitch;
  const flatSwitch =
    swtName === 'ess'
      ? essData.flatEssSwitch
      : swtName === 'tgs'
      ? tgsData.flatTgsSwitch
      : tesData.flatTesSwitch;

  const { messageBoxHandler } = useContext(EssTgsTesContext);
  const dispatch = useDispatch();

  const {
    handleOpenMachineController,
    handleUnSelectIndividualMachine,
    specificLocationUnselectMachinesHandler,
  } = SYSTEM_HANDLERS[swtName];

  const [searchParams, setSearchParams] = useSearchParams();
  const lastHandledSwitchRef = useRef(null);

  useEffect(() => {
    const switchParam = searchParams.get('switch');
    if (!switchParam || lastHandledSwitchRef.current === switchParam) return;

    const parts = switchParam.split(' - ');
    const [location, second, third] = parts;
    const payload =
      parts.length === 3
        ? { location, specificLocation: second, machine: third, status: true }
        : parts.length === 2
        ? { location, machine: second, status: true }
        : null;

    const machineExists = payload
      ? payload.specificLocation
        ? !!flatSwitch?.[payload.location]?.[payload.specificLocation]?.[payload.machine]
        : !!flatSwitch?.[payload.location]?.[payload.machine]
      : false;

    if (!machineExists) return;

    dispatch(handleOpenMachineController(payload));
    lastHandledSwitchRef.current = switchParam;

    // the search box only names the switch and its flat location, so the parent
    // location of a sub-location switch has to come back off the hash
    const hash = new URLSearchParams(window.location.hash.replace('#', ''));
    const hashSpecificLocation = hash.get('specificLocation');

    const selection = payload.specificLocation
      ? { location: payload.location, zone: payload.specificLocation }
      : hashSpecificLocation === payload.location
      ? { location: hash.get('location') || payload.location, zone: hashSpecificLocation }
      : { location: payload.location, zone: null };

    const next = new URLSearchParams(searchParams);
    next.delete('switch');
    // the panel reads its selection from these, so they have to land in the same
    // update that clears `switch` or one write would drop the other
    next.set('location', selection.location);
    if (selection.zone) {
      next.set('zone', selection.zone);
    } else {
      next.delete('zone');
    }
    next.set('machine', payload.machine);
    setSearchParams(next, { replace: true });
  }, [searchParams, flatSwitch, dispatch, handleOpenMachineController, setSearchParams]);

  useGetScheduleQueries(flatSwitch, swtName.toUpperCase());

  useGetThermocouplesQueries(swtName !== 'tgs' ? flatSwitch : {}, swtName);
  useSetOpenMasterControl(swtName, isMobile);
  useSetZoneOpeningsState(flatSwitch, masterControl, handleOpenMachineController, swtName);


  const { data: essZones } = useQuery(
    ['essZones', 'structured'],
    () => getEssZones({ structured: true }).then(filterZonesWithDevices),
    { enabled: swtName === 'ess' && !!handleAccessToken, staleTime: Infinity }
  );
  const { data: tgsZones } = useQuery(
    ['tgsZones', 'structured'],
    () => getTgsZones({ structured: true }).then(filterZonesWithDevices),
    { enabled: swtName === 'tgs' && !!handleAccessToken, staleTime: Infinity }
  );
  const { data: tesZones } = useQuery(
    ['tesZones', 'structured'],
    () => getTesZones({ structured: true }).then(filterZonesWithDevices),
    { enabled: swtName === 'tes' && !!handleAccessToken, staleTime: Infinity }
  );
  const zones = swtName === 'ess' ? essZones : swtName === 'tgs' ? tgsZones : tesZones;

  const integratedButtonHandler = (id, state, scope, temp, data, type, specificLocation) => {
    if (!permissions.WRITE) return;
    loopMachinesHandler(
      id,
      state,
      scope,
      type,
      temp,
      data,
      isF,
      zones,
      swtName.toUpperCase(),
      flatSwitch,
      dispatch,
      handleUnSelectIndividualMachine,
      specificLocationUnselectMachinesHandler,
      handleResetAllSelectBySwitch,
      handleResetAllSelectByLocation,
      messageBoxHandler,
      switchData
    );
  };

  return (
    <Wrapper>
      {isMasterControl || (
        <Section>
          <MasterControlBySwitch swtName={swtName} buttonHandler={integratedButtonHandler} />
        </Section>
      )}
      <Section>
        <SwitchLayout swtName={swtName} isMasterControl={isMasterControl} buttonHandler={integratedButtonHandler} />
      </Section>
    </Wrapper>
  );
};

export default SwitchMain;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const Section = styled.section`
  &:first-child {
    margin-bottom: 8px;
  }
`;
